import * as Sentry from '@sentry/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { chooseIceServers, type IceSelection } from '@/lib/ice-preflight';
import {
  consoleLogIceSelection,
  consoleLogIceServers,
  parseIceUrl,
  summarizeIceServers,
} from '@/lib/ice-preflight-logs';
import {
  type CandidateCounters,
  clearCurrentCall,
  emptyCandidateCounters,
  fmt,
  generateCallId,
  getCallId,
  setCurrentCall,
  tallyCandidate,
  wlog,
} from '@/lib/webrtc-logger';
import {
  type CallAcceptedMessage,
  type CallInProgressMessage,
  CallState,
} from '@/types/websocket-call-messages';
import { useMediaDevices } from './use-media-devices';
import {
  FALLBACK_ICE_SERVERS,
  useWebRTCMedia,
  wasRolledBack,
} from './use-webrtc-media';
import { useWebSocketCall } from './use-websocket-call';

const WEBSOCKET_URL = import.meta.env.VITE_WEB_SOCKET_URL;

interface CallHookState {
  callState: CallState;
}

interface CallConnectionIssue {
  title: string;
  message: string;
  advice: string[];
}

type CallAction =
  | { type: 'SET_CALL_STATE'; payload: CallState }
  | { type: 'SET_CALL_ID'; payload: string | null }
  | { type: 'RESET_CALL' };

const initialState: CallHookState = {
  callState: CallState.IDLE,
};

/**
 * Reducer function to manage call state.
 * Handles actions related to call state changes and resets.
 * Mostly works via TypeScript validation, rather than runtime checks.
 *
 * TODO: Improve run-time validation of actions
 */
function callReducer(state: CallHookState, action: CallAction): CallHookState {
  switch (action.type) {
    case 'SET_CALL_STATE':
      return { ...state, callState: action.payload };
    case 'RESET_CALL':
      return { ...initialState };
    default:
      return state;
  }
}

type TeardownSource =
  | 'terminal_failure'
  | 'server_end_call'
  | 'device_end_call'
  | 'user_hangup'
  | 'modal_close'
  | 'ws_disconnect';

type RemoteIceCandidate = RTCIceCandidateInit &
  Partial<Pick<RTCIceCandidate, 'protocol' | 'type'>>;

const CONNECTED_CALL_STATES: readonly CallState[] = [
  CallState.ACCEPTED,
  CallState.IN_PROGRESS,
];

const NETWORK_RESTRICTION_ISSUE: CallConnectionIssue = {
  title: 'Call could not connect',
  message:
    'Your network may be blocking real-time calling traffic. Ask your IT team to allow WebRTC/TURN traffic for calling, especially TCP/TLS on port 443.',
  advice: [
    'Try again from a different network if one is available.',
    'Avoid VPNs while placing Alexa Smart Properties calls.',
    'If this is a managed network, ask IT to allow TURN over TCP/TLS on port 443.',
  ],
};

const PREFLIGHT_DEGRADED_ISSUE: CallConnectionIssue = {
  title: 'Calling may not work on this network',
  message:
    'We could not reach any STUN or TURN server during pre-call checks. Calls placed from this network are likely to fail until WebRTC traffic is allowed.',
  advice: [
    'Try again from a different network if one is available.',
    'Avoid VPNs while placing Alexa Smart Properties calls.',
    'If this is a managed network, ask IT to allow TURN over TCP/TLS on port 443.',
  ],
};

export function useCall(unitId: number, isActive: boolean) {
  const [state, dispatch] = useReducer(callReducer, initialState);
  const [isSwitchingDevices, setIsSwitchingDevices] = useState(false);
  const [iceSelection, setIceSelection] = useState<IceSelection | null>(null);
  const [icePreflightState, setIcePreflightState] = useState<
    'idle' | 'probing' | 'ready'
  >('idle');
  /**
   * Per-modal-session URL overrides. Any URL in this set is filtered out
   * of `effectiveIceSelection.iceServers` before the PC is created, even
   * if preflight passed it. Reset whenever a new `IceSelection` arrives
   * (preflight reran) so stale overrides don't leak across sessions.
   */
  const [excludedIceUrls, setExcludedIceUrls] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleIceUrlExclusion = useCallback((url: string) => {
    setExcludedIceUrls((current) => {
      const next = new Set(current);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  }, []);
  const [connectionIssue, setConnectionIssue] =
    useState<CallConnectionIssue | null>(null);
  const hasTerminatedCallRef = useRef(false);
  const callStateRef = useRef(state.callState);
  const webRTCCleanupRef = useRef<() => void>(() => {});
  /**
   * Refs pointing at the latest `useWebRTCMedia` accessors so
   * `performCallTeardown` can surface per-call state (did an answer
   * arrive? what did local gathering look like?) without depending on
   * `webRTC` directly, which would create a circular dep — the hook
   * itself takes `performCallTeardown` via `onConnectionFailure`.
   */
  const getAnswerReceivedRef = useRef<() => boolean>(() => false);
  const getLocalCandidateCountsRef = useRef<() => CandidateCounters>(() =>
    emptyCandidateCounters(),
  );
  /**
   * Wall-clock start of the current call attempt. Set in `createOffer`;
   * reset on every new call so `ms_since_call_initiated` in the
   * teardown log reflects just this attempt.
   */
  const callInitiatedAtRef = useRef<number | null>(null);
  /**
   * Ensures the `First remote ICE candidate added` info log fires at
   * most once per call — diagnostically we only need to know whether
   * remote candidates ever arrived, not every tick.
   */
  const firstRemoteCandidateLoggedRef = useRef(false);
  const remoteCandidateCountersRef = useRef<CandidateCounters>(
    emptyCandidateCounters(),
  );
  const pendingRemoteCandidatesRef = useRef<RemoteIceCandidate[]>([]);

  /**
   * Sub-hooks to manage the websocket connection and the
   * media+webrtc functionality.
   */
  const webSocket = useWebSocketCall(WEBSOCKET_URL, isActive);
  const {
    isConnected: wsIsConnected,
    sendMessage: wsSendMessage,
    iceServers: wsIceServers,
  } = webSocket;
  const mediaDevices = useMediaDevices(isActive);

  const effectiveIceSelection = useMemo<{
    iceServers: RTCIceServer[];
    iceTransportPolicy: RTCIceTransportPolicy;
    profileId: 'first-offer-mixed' | 'fallback-google-stun';
  }>(() => {
    const base = iceSelection ?? {
      iceServers: FALLBACK_ICE_SERVERS,
      iceTransportPolicy: 'all' as RTCIceTransportPolicy,
      profileId: 'fallback-google-stun' as const,
    };
    if (excludedIceUrls.size === 0) return base;
    const filteredIceServers = base.iceServers.filter((server) => {
      const url = Array.isArray(server.urls) ? server.urls[0] : server.urls;
      return !excludedIceUrls.has(url);
    });
    return { ...base, iceServers: filteredIceServers };
  }, [iceSelection, excludedIceUrls]);

  const webRTCIceOptions = useMemo(
    () => ({
      iceServers: effectiveIceSelection.iceServers,
      iceTransportPolicy: effectiveIceSelection.iceTransportPolicy,
      iceProfileId: effectiveIceSelection.profileId,
    }),
    [effectiveIceSelection],
  );

  const performCallTeardown = useCallback(
    ({
      notifyRemote,
      teardownSource,
      failureReason,
      connectionContext,
    }: {
      notifyRemote: boolean;
      teardownSource: TeardownSource;
      failureReason?: string;
      connectionContext?: unknown;
    }) => {
      if (hasTerminatedCallRef.current) {
        return;
      }
      // Nothing to tear down if the call never left IDLE — emitting a
      // teardown log for a call that was never placed is noise.
      if (
        callStateRef.current === CallState.IDLE &&
        teardownSource !== 'terminal_failure'
      ) {
        return;
      }
      hasTerminatedCallRef.current = true;

      // Snapshot per-call state BEFORE webRTC cleanup, because
      // `cleanup()` resets refs the snapshot reads (notably
      // `answerReceivedRef`). Reading them after teardown would make
      // every `Call teardown` log report `answer_received: false`,
      // defeating the point of the attribute.
      const answerReceived = getAnswerReceivedRef.current();
      const localCounts = { ...getLocalCandidateCountsRef.current() };
      const remoteCounts = { ...remoteCandidateCountersRef.current };
      const wasConnected = CONNECTED_CALL_STATES.includes(callStateRef.current);
      const msSinceCallInitiated = callInitiatedAtRef.current
        ? Date.now() - callInitiatedAtRef.current
        : null;
      const inferredNetworkRestriction =
        teardownSource === 'terminal_failure' &&
        answerReceived &&
        (failureReason === 'connection_timeout' ||
          failureReason === 'ice_failed' ||
          localCounts.relay === 0);

      if (inferredNetworkRestriction) {
        setConnectionIssue(NETWORK_RESTRICTION_ISSUE);
      }

      webRTCCleanupRef.current();

      const level: 'info' | 'warn' | 'error' =
        teardownSource === 'terminal_failure'
          ? 'error'
          : teardownSource === 'user_hangup' || teardownSource === 'modal_close'
            ? 'info'
            : 'warn';

      wlog(level, fmt`Call teardown: ${teardownSource}`, {
        component: 'use-call',
        teardown_source: teardownSource,
        failure_reason: failureReason ?? null,
        call_state: callStateRef.current,
        was_connected: wasConnected,
        answer_received: answerReceived,
        ms_since_call_initiated: msSinceCallInitiated,
        local_candidate_total: localCounts.total,
        local_host: localCounts.host,
        local_srflx: localCounts.srflx,
        local_relay: localCounts.relay,
        remote_candidate_total: remoteCounts.total,
        remote_host: remoteCounts.host,
        remote_srflx: remoteCounts.srflx,
        remote_relay: remoteCounts.relay,
        pending_remote_candidate_total:
          pendingRemoteCandidatesRef.current.length,
        inferred_network_restriction: inferredNetworkRestriction,
        ice_profile_id: effectiveIceSelection.profileId,
        ws_connected: webSocket.isConnected,
        ws_connection_state: webSocket.connectionState,
        ws_ready_state: webSocket.getReadyState(),
        ...(connectionContext && typeof connectionContext === 'object'
          ? (connectionContext as Record<string, unknown>)
          : {}),
      });

      if (notifyRemote && webSocket.isConnected) {
        webSocket.sendMessage({
          action: 'dmp-end-call',
          unitId,
        });
      }

      dispatch({ type: 'SET_CALL_STATE', payload: CallState.ENDED });
      clearCurrentCall();
    },
    [effectiveIceSelection.profileId, unitId, webSocket],
  );
  const performCallTeardownRef = useRef(performCallTeardown);
  useEffect(() => {
    performCallTeardownRef.current = performCallTeardown;
  }, [performCallTeardown]);

  const webRTC = useWebRTCMedia(
    (offer: RTCSessionDescription) => {
      if (!unitId) {
        wlog('error', 'Attempted to send offer but unitId is undefined', {
          component: 'use-call',
        });
        return;
      }
      wlog('info', 'Sending SDP offer via signaling', {
        component: 'use-call',
        sdp_length: offer.sdp?.length ?? 0,
      });
      webSocket.sendMessage({
        action: 'offer',
        offer: { type: 'offer', sdp: offer.sdp },
        unitId,
      });
    },
    isActive,
    webRTCIceOptions,
    (reason, context) => {
      performCallTeardown({
        notifyRemote: true,
        teardownSource: 'terminal_failure',
        failureReason: reason,
        connectionContext: context,
      });
    },
  );
  webRTCCleanupRef.current = webRTC.cleanup;
  getAnswerReceivedRef.current = webRTC.getAnswerReceived;
  getLocalCandidateCountsRef.current = webRTC.getLocalCandidateCounts;

  const addRemoteIceCandidate = useCallback(
    async (candidate: RemoteIceCandidate) => {
      const peerConnection = webRTC.getPeerConnection();
      if (!peerConnection || !peerConnection.remoteDescription) {
        pendingRemoteCandidatesRef.current.push(candidate);
        wlog(
          'debug',
          'Queued remote ICE candidate until SDP answer is applied',
          {
            component: 'use-call',
            pending_remote_candidate_total:
              pendingRemoteCandidatesRef.current.length,
          },
        );
        return;
      }

      try {
        await peerConnection.addIceCandidate(candidate);
        if (!firstRemoteCandidateLoggedRef.current) {
          firstRemoteCandidateLoggedRef.current = true;
          wlog('info', 'First remote ICE candidate added', {
            component: 'use-call',
            candidate_type: candidate.type,
            candidate_protocol: candidate.protocol,
            flushed_from_queue: false,
          });
        }
      } catch (error) {
        wlog('error', 'Failed to add remote ICE candidate', {
          component: 'use-call',
          candidate_type: candidate.type,
          candidate_protocol: candidate.protocol,
          error_name: error instanceof Error ? error.name : 'Unknown',
          error_message: error instanceof Error ? error.message : String(error),
        });
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'video-calling');
          scope.setTag('component', 'use-call');
          const callId = getCallId();
          if (callId) scope.setTag('call_id', callId);
          Sentry.captureException(error);
        });
      }
    },
    [webRTC],
  );

  const flushPendingRemoteCandidates = useCallback(async () => {
    const peerConnection = webRTC.getPeerConnection();
    if (!peerConnection?.remoteDescription) {
      return;
    }

    const pending = pendingRemoteCandidatesRef.current.splice(0);
    if (pending.length === 0) {
      return;
    }

    wlog('info', fmt`Flushing ${pending.length} queued remote ICE candidates`, {
      component: 'use-call',
      pending_remote_candidate_total: pending.length,
    });

    for (const candidate of pending) {
      try {
        await peerConnection.addIceCandidate(candidate);
        if (!firstRemoteCandidateLoggedRef.current) {
          firstRemoteCandidateLoggedRef.current = true;
          wlog('info', 'First remote ICE candidate added', {
            component: 'use-call',
            candidate_type: candidate.type,
            candidate_protocol: candidate.protocol,
            flushed_from_queue: true,
          });
        }
      } catch (error) {
        wlog('error', 'Failed to add queued remote ICE candidate', {
          component: 'use-call',
          candidate_type: candidate.type,
          candidate_protocol: candidate.protocol,
          error_name: error instanceof Error ? error.name : 'Unknown',
          error_message: error instanceof Error ? error.message : String(error),
        });
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'video-calling');
          scope.setTag('component', 'use-call');
          const callId = getCallId();
          if (callId) scope.setTag('call_id', callId);
          Sentry.captureException(error);
        });
      }
    }
  }, [webRTC]);

  /**
   * Message handlers for WebSocket events related to calls.
   */
  const handleCallRinging = useCallback(() => {
    dispatch({ type: 'SET_CALL_STATE', payload: CallState.RINGING });
  }, []);

  const handleCallInProgress = useCallback(
    async (message: CallInProgressMessage) => {
      dispatch({ type: 'SET_CALL_STATE', payload: CallState.IN_PROGRESS });
      const applied = await webRTC.handleAnswer(message);
      if (applied) {
        await flushPendingRemoteCandidates();
      }
    },
    [flushPendingRemoteCandidates, webRTC],
  );

  const handleCallAccepted = useCallback(
    async (message: CallAcceptedMessage) => {
      dispatch({ type: 'SET_CALL_STATE', payload: CallState.ACCEPTED });
      const applied = await webRTC.handleAnswer(message);
      if (applied) {
        await flushPendingRemoteCandidates();
      }
    },
    [flushPendingRemoteCandidates, webRTC],
  );

  const handleCallUpdated = useCallback(() => {}, []);

  const handleCallUpdateAck = useCallback(() => {}, []);

  const handleCallUpdatedResponse = useCallback(() => {}, []);

  const handleCallAcceptedAck = useCallback(() => {}, []);

  const handleEndCall = useCallback(() => {
    performCallTeardown({
      notifyRemote: false,
      teardownSource: 'server_end_call',
    });
  }, [performCallTeardown]);

  const handleDeviceEndCall = useCallback(() => {
    performCallTeardown({
      notifyRemote: false,
      teardownSource: 'device_end_call',
    });
  }, [performCallTeardown]);

  const handleIceServers = useCallback(
    (message: { action: string; iceServers: RTCIceServer[] }) => {
      wlog(
        'info',
        fmt`Received ${message.iceServers?.length ?? 0} ICE servers from signaling`,
        {
          component: 'use-call',
          signaling_ice_server_count: message.iceServers?.length ?? 0,
        },
      );
      consoleLogIceServers(
        'received ICE servers from signaling',
        message.iceServers ?? [],
      );
      // The WebSocket hook stores these servers in state. That state drives
      // preflight selection; do not overwrite the active peer connection here
      // with a mixed server list, because ASP needs one complete non-trickle
      // profile before the SDP offer is created.
    },
    [],
  );

  const handleIceCandidate = useCallback(
    (message: { candidate?: RemoteIceCandidate | null }) => {
      if (!message.candidate) {
        return;
      }

      tallyCandidate(remoteCandidateCountersRef.current, message.candidate);
      void addRemoteIceCandidate(message.candidate);
    },
    [addRemoteIceCandidate],
  );

  // Register message handlers when WebSocket becomes available
  useEffect(() => {
    if (webSocket.isConnected) {
      webSocket.addMessageHandler('callRinging', handleCallRinging);
      webSocket.addMessageHandler('callInProgress', handleCallInProgress);
      webSocket.addMessageHandler('callAccepted', handleCallAccepted);
      webSocket.addMessageHandler('callUpdated', handleCallUpdated);
      webSocket.addMessageHandler('callUpdateAck', handleCallUpdateAck);
      webSocket.addMessageHandler('callUpdatedResponse', handleCallUpdatedResponse); // prettier-ignore
      webSocket.addMessageHandler('callAcceptedAck', handleCallAcceptedAck);
      webSocket.addMessageHandler('endCall', handleEndCall);
      webSocket.addMessageHandler('device-end-call', handleDeviceEndCall);
      webSocket.addMessageHandler('ice-candidate', handleIceCandidate);
      webSocket.addMessageHandler('ice-servers', handleIceServers);
    }

    return () => {
      // Cleanup message handlers
      webSocket.removeMessageHandler('callRinging');
      webSocket.removeMessageHandler('callInProgress');
      webSocket.removeMessageHandler('callAccepted');
      webSocket.removeMessageHandler('callUpdated');
      webSocket.removeMessageHandler('callUpdateAck');
      webSocket.removeMessageHandler('callUpdatedResponse');
      webSocket.removeMessageHandler('callAcceptedAck');
      webSocket.removeMessageHandler('endCall');
      webSocket.removeMessageHandler('device-end-call');
      webSocket.removeMessageHandler('ice-candidate');
      webSocket.removeMessageHandler('ice-servers');
    };
  }, [
    webSocket,
    webRTC,
    handleCallRinging,
    handleCallInProgress,
    handleCallAccepted,
    handleCallUpdated,
    handleCallUpdateAck,
    handleCallUpdatedResponse,
    handleCallAcceptedAck,
    handleEndCall,
    handleDeviceEndCall,
    handleIceCandidate,
    handleIceServers,
  ]);

  // Request ICE servers as soon as the WebSocket connects
  useEffect(() => {
    if (wsIsConnected) {
      wsSendMessage({ action: 'get-ice-servers' });
    }
  }, [wsIsConnected, wsSendMessage]);

  useEffect(() => {
    if (!isActive) {
      setIceSelection(null);
      setIcePreflightState('idle');
      setConnectionIssue(null);
      return;
    }

    if (!wsIsConnected || wsIceServers.length === 0) {
      setIceSelection(null);
      setIcePreflightState('idle');
      return;
    }

    let cancelled = false;
    setIcePreflightState('probing');

    void chooseIceServers(wsIceServers, FALLBACK_ICE_SERVERS)
      .then((selection) => {
        if (cancelled) return;
        setIceSelection(selection);
        // A new preflight run is the natural reset point for user
        // overrides — keeping them across reruns would silently exclude
        // URLs the user has long forgotten about.
        setExcludedIceUrls(new Set());
        setIcePreflightState('ready');
        if (selection.reason === 'degraded_no_usable_candidates') {
          setConnectionIssue(PREFLIGHT_DEGRADED_ISSUE);
        } else {
          // Clear a stale degraded warning from a previous preflight in the
          // same modal session (e.g. ICE servers were refreshed and this run
          // succeeded). Leave issues set by real call failures alone.
          setConnectionIssue((current) =>
            current === PREFLIGHT_DEGRADED_ISSUE ? null : current,
          );
        }
        const selectedSummary = summarizeIceServers(selection.iceServers);
        const selectedUrls = selection.iceServers.flatMap((server) =>
          Array.isArray(server.urls) ? server.urls : [server.urls],
        );
        wlog('info', fmt`ICE preflight finished: ${selection.reason}`, {
          component: 'use-call',
          ice_profile_id: selection.profileId,
          ice_preflight_reason: selection.reason,
          ice_transport_policy: selection.iceTransportPolicy,
          // Survivors — what the real PC will actually be configured with.
          // `ice_url_results` below is the *full* probe set; if a URL is in
          // results but not in selected_urls, it was filtered as unusable.
          ice_selected_count: selectedUrls.length,
          ice_selected_urls: selectedUrls,
          ice_selected_stun: selectedSummary.stun,
          ice_selected_turn: selectedSummary.turn,
          ice_selected_turns: selectedSummary.turns,
          ice_selected_udp: selectedSummary.udp,
          ice_selected_tcp: selectedSummary.tcp,
          ice_url_results: selection.results.map((result) => ({
            url: result.url,
            transport: parseIceUrl(result.url)?.transport ?? null,
            elapsed_ms: result.elapsedMs,
            timed_out: result.timedOut,
            error_count: result.errorCount,
            candidate_host: result.counters.host,
            candidate_srflx: result.counters.srflx,
            candidate_relay: result.counters.relay,
            protocol_udp: result.counters.udp,
            protocol_tcp: result.counters.tcp,
          })),
        });
      })
      .catch((error) => {
        if (cancelled) return;
        const fallbackSelection: IceSelection = {
          iceServers: FALLBACK_ICE_SERVERS,
          iceTransportPolicy: 'all',
          reason: 'preflight_threw_fallback_to_google_stun',
          results: [],
          blockedByBrowser: [],
          profileId: 'fallback-google-stun',
        };
        setIceSelection(fallbackSelection);
        setExcludedIceUrls(new Set());
        setIcePreflightState('ready');
        // Preflight threw entirely — the fallback route is no more reliable
        // than the no-usable-candidates path, so surface the same warning.
        setConnectionIssue(PREFLIGHT_DEGRADED_ISSUE);
        consoleLogIceSelection(fallbackSelection);
        wlog('warn', 'ICE preflight threw; falling back to Google STUN', {
          component: 'use-call',
          ice_profile_id: fallbackSelection.profileId,
          ice_preflight_reason: fallbackSelection.reason,
          error_name: error instanceof Error ? error.name : 'Unknown',
          error_message: error instanceof Error ? error.message : String(error),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isActive, wsIceServers, wsIsConnected]);

  // Create peer connection when WebSocket is connected, isActive, AND ICE servers are available
  // This prevents the race condition where the peer connection was created before
  // ICE servers arrived, causing it to fall back to Google STUN servers.
  useEffect(() => {
    if (wsIsConnected && isActive && icePreflightState === 'ready') {
      webRTC.createPeerConnection();
    }
  }, [wsIsConnected, isActive, icePreflightState, webRTC]);

  // Run cleanup exactly once when the call is de‑activated (or when the component unmounts).
  // Routed through `performCallTeardown` so a modal-close that interrupts an
  // in-progress call produces a `Call teardown` log with `teardown_source:
  // 'modal_close'` — otherwise it would be one of the silent-death paths.
  useEffect(() => {
    if (!isActive) return;

    return () => {
      performCallTeardownRef.current({
        notifyRemote: false,
        teardownSource: 'modal_close',
      });
      dispatch({ type: 'RESET_CALL' });
      clearCurrentCall();
    };
  }, [isActive]);

  // Track the previous WebSocket connection state for mid-call disconnect detection
  const prevIsConnectedRef = useRef(webSocket.isConnected);

  // Detect mid-call WebSocket disconnection. If the WS drops during an
  // active call the signalling link is gone — we can't receive the
  // remote's `dmp-end-call` or ICE candidates, so continuing to sit in
  // that call is meaningless. Route through `performCallTeardown` with
  // `ws_disconnect` so the teardown is visible in Sentry alongside the
  // other silent-death paths.
  useEffect(() => {
    const wasConnected = prevIsConnectedRef.current;
    prevIsConnectedRef.current = webSocket.isConnected;

    if (wasConnected && !webSocket.isConnected) {
      const activeCallStates = [
        CallState.PLACING,
        CallState.RINGING,
        CallState.IN_PROGRESS,
        CallState.ACCEPTED,
      ];

      if (activeCallStates.includes(state.callState)) {
        performCallTeardown({
          notifyRemote: false,
          teardownSource: 'ws_disconnect',
        });
      }
    }
  }, [webSocket.isConnected, state.callState, unitId, performCallTeardown]);

  // Log call state transitions
  useEffect(() => {
    callStateRef.current = state.callState;
    wlog('info', fmt`Call state: ${state.callState}`, {
      component: 'use-call',
      call_state: state.callState,
    });
  }, [state.callState, unitId]);

  // Enumerate devices when modal becomes active (manual trigger)
  useEffect(() => {
    /**
     * `useMediaDevices` returns a plain object literal on every render,
     * so `mediaDevices` is an unstable reference and this effect fires
     * on every re-render. That's fine as long as the body is idempotent.
     *
     * The `!deviceError` guard is critical: without it, a permission or
     * NotReadableError failure leaves videos/audios empty, the effect
     * re-fires, and we enter an infinite enumerate → fail → enumerate
     * loop that makes the device panel flash between "Detecting
     * devices…" and the red error overlay. After a failure, the user
     * must click Retry (which calls `enumerateDevices` directly,
     * bypassing this guard).
     */
    if (
      isActive &&
      !mediaDevices.deviceError &&
      mediaDevices.videoDevices.length === 0 &&
      mediaDevices.audioDevices.length === 0 &&
      !mediaDevices.isEnumerating
    ) {
      mediaDevices.enumerateDevices();
    }
  }, [isActive, mediaDevices]);

  const createOffer = useCallback(async () => {
    hasTerminatedCallRef.current = false;
    setConnectionIssue(null);
    setCurrentCall({ callId: generateCallId(), unitId });
    remoteCandidateCountersRef.current = {
      ...emptyCandidateCounters(),
    };
    pendingRemoteCandidatesRef.current = [];
    firstRemoteCandidateLoggedRef.current = false;
    callInitiatedAtRef.current = Date.now();
    const callUrls = effectiveIceSelection.iceServers.flatMap((server) =>
      Array.isArray(server.urls) ? server.urls : [server.urls],
    );
    console.info('[WebRTC] placing call with ICE servers', {
      profileId: effectiveIceSelection.profileId,
      iceTransportPolicy: effectiveIceSelection.iceTransportPolicy,
      urlCount: callUrls.length,
      urls: callUrls,
    });
    wlog('info', 'Call initiated by local user', {
      component: 'use-call',
      ice_profile_id: effectiveIceSelection.profileId,
    });
    dispatch({ type: 'SET_CALL_STATE', payload: CallState.PLACING });
    try {
      await webRTC.createOffer();
    } catch (error) {
      wlog('error', 'Call failed during offer creation', {
        component: 'use-call',
        error_name: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : String(error),
      });
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'use-call');
        const callId = getCallId();
        if (callId) scope.setTag('call_id', callId);
        Sentry.captureException(error);
      });
      clearCurrentCall();
      dispatch({ type: 'SET_CALL_STATE', payload: CallState.IDLE });
    }
  }, [effectiveIceSelection.profileId, webRTC, unitId]);

  const cleanupCall = useCallback(() => {
    hasTerminatedCallRef.current = false;
    dispatch({ type: 'RESET_CALL' });
    clearCurrentCall();
  }, []);

  const endCall = useCallback(() => {
    performCallTeardown({
      notifyRemote: true,
      teardownSource: 'user_hangup',
    });
  }, [performCallTeardown]);

  const switchDevices = useCallback(
    async (videoDeviceId?: string, audioDeviceId?: string) => {
      if (isSwitchingDevices) {
        return;
      }

      try {
        setIsSwitchingDevices(true);

        /**
         * Compute the new constraints from the explicit override
         * arguments so we never touch `selectedVideoDevice` /
         * `selectedAudioDevice` before the switch has actually
         * succeeded. This guarantees that if the hardware switch
         * fails, the selector UI still reflects whatever device the
         * live stream is actually using.
         */
        const newConstraints = mediaDevices.getDeviceConstraints(
          videoDeviceId,
          audioDeviceId,
        );

        await webRTC.switchDevices(newConstraints);

        // The switch succeeded — now it's safe to advance the selector.
        if (videoDeviceId) {
          mediaDevices.selectVideoDevice(videoDeviceId);
        }
        if (audioDeviceId) {
          mediaDevices.selectAudioDevice(audioDeviceId);
        }
      } catch (error) {
        wlog('error', 'Device switch failed at call layer', {
          component: 'use-call',
          error_name: error instanceof Error ? error.name : 'Unknown',
          error_message: error instanceof Error ? error.message : String(error),
          rolled_back: wasRolledBack(error),
        });
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'video-calling');
          scope.setTag('component', 'use-call');
          const callId = getCallId();
          if (callId) scope.setTag('call_id', callId);
          Sentry.captureException(error);
        });

        /**
         * Two failure modes:
         * - rollback succeeded → the stream is still live on the
         *   previous device, so `mediaError` is (correctly) null and
         *   the place-call overlay won't show. Fire a toast here so
         *   the user still gets feedback that their request failed.
         * - rollback failed → `useWebRTCMedia` has set `mediaError`,
         *   and the place-call rising-edge effect will already toast
         *   + render the overlay. Skip the toast here to avoid
         *   double-firing.
         */
        if (wasRolledBack(error)) {
          const deviceLabel = videoDeviceId
            ? 'camera'
            : audioDeviceId
              ? 'microphone'
              : 'device';
          toast.error(`Couldn't switch ${deviceLabel}`, {
            description:
              'We restored your previous device. Close any other app that might be using it, then try again.',
          });
        }

        throw error;
      } finally {
        setIsSwitchingDevices(false);
      }
    },
    [mediaDevices, webRTC, isSwitchingDevices],
  );

  const hasIceServers = webSocket.iceServers.length > 0;
  const hasNetworkRoute = icePreflightState === 'ready';

  const isReadyToCall =
    webSocket.isConnected &&
    webRTC.isStreamReady &&
    hasIceServers &&
    hasNetworkRoute &&
    state.callState === CallState.IDLE;

  const canEditIceSelection =
    state.callState === CallState.IDLE && icePreflightState === 'ready';

  return {
    // State
    callState: state.callState,
    connectionState: webSocket.connectionState,
    hasIceServers,
    isWebSocketConnected: webSocket.isConnected,
    isStreamReady: webRTC.isStreamReady,
    isReadyToCall,
    isSwitchingDevices,
    mediaError: webRTC.mediaError,
    connectionIssue,
    icePreflightState,
    iceSelection,
    excludedIceUrls,
    canEditIceSelection,
    selectedIceProfileId: effectiveIceSelection.profileId,

    // Actions
    placeCall: createOffer,
    cleanupCall,
    endCall,
    switchDevices,
    retryMediaStream: webRTC.retryMediaStream,
    toggleIceUrlExclusion,

    // Refs for video elements
    videoRef: webRTC.videoRef,
    remoteVideoRef: webRTC.remoteVideoRef,
    peerConnection: webRTC.peerConnection,

    // Media devices
    mediaDevices: {
      videoDevices: mediaDevices.videoDevices,
      audioDevices: mediaDevices.audioDevices,
      selectedVideoDevice: mediaDevices.selectedVideoDevice,
      selectedAudioDevice: mediaDevices.selectedAudioDevice,
      selectVideoDevice: mediaDevices.selectVideoDevice,
      selectAudioDevice: mediaDevices.selectAudioDevice,
      enumerateDevices: mediaDevices.enumerateDevices,
      isEnumerating: mediaDevices.isEnumerating,
      hasDevicePermission: mediaDevices.hasDevicePermission,
      deviceError: mediaDevices.deviceError,
    },
  };
}
